import { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { documentsAPI, analysisAPI, paymentsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { 
    Shield, 
    Upload, 
    FileText, 
    Trash2, 
    ArrowRight, 
    Sparkles,
    Lock,
    CheckCircle2,
    AlertCircle,
    LogOut,
    CreditCard,
    ListChecks,
    RefreshCw,
    Settings,
    Gift
} from "lucide-react";
import { toast } from "sonner";
import PricingModal from "../components/PricingModal";
import AccountControls from "../components/AccountControls";
import DisputeTimeline from "../components/DisputeTimeline";
import CampaignProgressCard from "../components/CampaignProgressCard";
import ReferralCard from "../components/ReferralCard";

const DashboardPage = () => {
    const navigate = useNavigate();
    const { user, logout, refreshUser } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [reanalyzing, setReanalyzing] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [pricing, setPricing] = useState({ price: null, formatted: '…', monthly_formatted: '…', one_time_formatted: '…' });
    const [pricingOpen, setPricingOpen] = useState(false);

    const loadPricing = useCallback(async () => {
        try {
            const response = await paymentsAPI.getPricing();
            setPricing(response.data);
        } catch (error) {
            // Pricing fetch is non-critical — defaults already set in state
        }
    }, []);

    const loadDocuments = useCallback(async () => {
        try {
            const response = await documentsAPI.getAll();
            setDocuments(response.data);
        } catch (error) {
            toast.error("Could not load your documents");
        }
    }, []);

    const loadAnalysis = useCallback(async () => {
        try {
            const response = await analysisAPI.getResults();
            setAnalysis(response.data);
        } catch (error) {
            // 404 when no analysis exists yet — expected for new users
        }
    }, []);

    useEffect(() => {
        loadDocuments();
        loadAnalysis();
        loadPricing();
    }, [loadDocuments, loadAnalysis, loadPricing]);

    const handleFileUpload = async (files) => {
        const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
        
        for (const file of files) {
            if (!allowedTypes.includes(file.type)) {
                toast.error(`Invalid file type: ${file.name}. Only PDF, PNG, and JPEG are allowed.`);
                continue;
            }

            setUploading(true);
            try {
                await documentsAPI.upload(file);
                toast.success(`Uploaded: ${file.name}`);
                await loadDocuments();
            } catch (error) {
                toast.error(`Failed to upload: ${file.name}`);
            }
        }
        setUploading(false);
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        handleFileUpload(files);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
    }, []);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        handleFileUpload(files);
    };

    const handleDeleteDocument = async (docId) => {
        try {
            await documentsAPI.delete(docId);
            toast.success("Document deleted");
            await loadDocuments();
        } catch (error) {
            toast.error("Failed to delete document");
        }
    };

    const handleAnalyze = async () => {
        if (documents.length === 0) {
            toast.error("Please upload at least one document first");
            return;
        }

        setAnalyzing(true);
        try {
            const response = await analysisAPI.analyze();
            setAnalysis(response.data);
            toast.success("Analysis complete!");
            navigate("/analysis");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Analysis failed");
        } finally {
            setAnalyzing(false);
        }
    };

    const handleReanalyze = async () => {
        if (documents.length === 0) {
            toast.error("Please upload at least one document first");
            return;
        }
        if (!window.confirm("Re-analyze your current documents? This replaces your existing analysis, letters, and dispute trackers with fresh ones.")) {
            return;
        }
        setReanalyzing(true);
        try {
            const response = await analysisAPI.reanalyze();
            setAnalysis(response.data);
            toast.success("Fresh analysis ready!");
            navigate("/analysis");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Re-analysis failed");
        } finally {
            setReanalyzing(false);
        }
    };

    const handleUpgrade = () => {
        setPricingOpen(true);
    };

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="nav-glass sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Shield className="h-8 w-8 text-primary" />
                        <span className="font-serif text-xl font-semibold">CreditFix AI</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        {user?.has_paid ? (
                            <Badge className="badge-premium">Full Access</Badge>
                        ) : (
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={handleUpgrade}
                                disabled={checkoutLoading}
                                className="rounded-full"
                                data-testid="upgrade-btn"
                            >
                                <Lock className="h-4 w-4 mr-2" />
                                Upgrade from {pricing.monthly_formatted || '…'}/mo
                            </Button>
                        )}
                        {user?.is_admin && (
                            <Link to="/admin">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full"
                                    data-testid="admin-nav-btn"
                                >
                                    <Settings className="h-4 w-4 mr-2" />
                                    Admin
                                </Button>
                            </Link>
                        )}
                        <Link to="/refer">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-full hover:bg-accent/10 hover:text-accent"
                                data-testid="refer-nav-btn"
                            >
                                <Gift className="h-4 w-4 mr-2" />
                                Share & earn
                            </Button>
                        </Link>
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={handleLogout}
                            data-testid="logout-btn"
                        >
                            <LogOut className="h-4 w-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                </div>
            </nav>

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                {/* Welcome Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <h1 className="text-3xl font-serif font-bold text-foreground mb-2">
                        Welcome back, {user?.full_name?.split(' ')[0]}
                    </h1>
                    <p className="text-muted-foreground">
                        Upload your credit reports to get started with your analysis.
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Live Campaign Timeline — paid users only */}
                        {user?.has_paid && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.05 }}
                            >
                                <DisputeTimeline />
                            </motion.div>
                        )}

                        {/* Upload Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                        >
                            <Card className="border-border">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 font-serif">
                                        <Upload className="h-5 w-5 text-primary" />
                                        Upload Credit Reports
                                    </CardTitle>
                                    <CardDescription>
                                        Upload your credit reports from Equifax, Experian, or TransUnion (PDF, PNG, JPEG)
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div
                                        className={`upload-zone rounded-xl p-8 text-center cursor-pointer ${dragOver ? 'drag-over' : ''}`}
                                        onDrop={handleDrop}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onClick={() => document.getElementById('file-input').click()}
                                        data-testid="upload-zone"
                                    >
                                        <input
                                            id="file-input"
                                            type="file"
                                            multiple
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            onChange={handleFileSelect}
                                            className="hidden"
                                            data-testid="file-input"
                                        />
                                        <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                        {uploading ? (
                                            <p className="text-muted-foreground">Uploading...</p>
                                        ) : (
                                            <>
                                                <p className="text-foreground font-medium mb-1">
                                                    Drag and drop your files here
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    or click to browse
                                                </p>
                                            </>
                                        )}
                                    </div>

                                    {/* Uploaded Documents */}
                                    {documents.length > 0 && (
                                        <div className="mt-6 space-y-3">
                                            <h4 className="text-sm font-medium text-foreground">Uploaded Documents</h4>
                                            {documents.map((doc) => (
                                                <div 
                                                    key={doc.document_id}
                                                    className="flex items-center justify-between p-3 rounded-lg document-card border border-border"
                                                    data-testid={`document-${doc.document_id}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <FileText className="h-5 w-5 text-primary" />
                                                        <div>
                                                            <p className="text-sm font-medium text-foreground">{doc.filename}</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {new Date(doc.upload_date).toLocaleDateString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteDocument(doc.document_id)}
                                                        className="text-destructive hover:text-destructive"
                                                        data-testid={`delete-${doc.document_id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </motion.div>

                        {/* Analyze Button */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <Button
                                size="lg"
                                className="w-full rounded-full btn-press"
                                onClick={handleAnalyze}
                                disabled={documents.length === 0 || analyzing}
                                data-testid="analyze-btn"
                            >
                                {analyzing ? (
                                    <span className="flex items-center gap-2">
                                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                        Analyzing your documents...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <Sparkles className="h-5 w-5" />
                                        Analyze My Credit Report
                                        <ArrowRight className="h-4 w-4" />
                                    </span>
                                )}
                            </Button>
                        </motion.div>

                        {/* Analysis Results Preview */}
                        {analysis && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                <Card className="border-border">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 font-serif">
                                            <CheckCircle2 className="h-5 w-5 text-accent" />
                                            Analysis Complete
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="text-center p-4 rounded-lg bg-muted">
                                                <p className="text-2xl font-bold font-mono text-foreground">
                                                    {analysis.issues_found?.length || 0}
                                                </p>
                                                <p className="text-sm text-muted-foreground">Issues Found</p>
                                            </div>
                                            <div className="text-center p-4 rounded-lg bg-muted">
                                                <p className="text-2xl font-bold font-mono text-foreground">
                                                    {analysis.letters_needed?.length || 0}
                                                </p>
                                                <p className="text-sm text-muted-foreground">Letters Needed</p>
                                            </div>
                                            <div className="text-center p-4 rounded-lg bg-muted">
                                                <p className="text-2xl font-bold font-mono text-foreground">
                                                    {analysis.action_plan?.length || 0}
                                                </p>
                                                <p className="text-sm text-muted-foreground">Action Steps</p>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-3">
                                            <div className="flex gap-4">
                                                <Link to="/analysis" className="flex-1">
                                                    <Button className="w-full rounded-full" data-testid="view-analysis-btn">
                                                        View Full Analysis
                                                    </Button>
                                                </Link>
                                                <Link to="/letters" className="flex-1">
                                                    <Button variant="outline" className="w-full rounded-full" data-testid="view-letters-btn">
                                                        View Letters
                                                    </Button>
                                                </Link>
                                            </div>
                                            {user?.has_paid && (
                                                <Link to="/disputes">
                                                    <Button variant="outline" className="w-full rounded-full border-accent text-accent hover:bg-accent/10" data-testid="track-disputes-btn">
                                                        <ListChecks className="h-4 w-4 mr-2" />
                                                        Track Disputes & Get Next Steps
                                                    </Button>
                                                </Link>
                                            )}
                                            {user?.has_paid && documents.length > 0 && (
                                                <Button
                                                    variant="ghost"
                                                    className="w-full rounded-full"
                                                    onClick={handleReanalyze}
                                                    disabled={reanalyzing}
                                                    data-testid="reanalyze-btn"
                                                >
                                                    {reanalyzing ? (
                                                        <span className="flex items-center gap-2">
                                                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                                                            Re-analyzing...
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <RefreshCw className="h-4 w-4 mr-2" />
                                                            Re-analyze with fresh report
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Referral card — visible to everyone (auto-hides if program is disabled) */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 }}
                        >
                            <ReferralCard />
                        </motion.div>

                        {/* Campaign Progress — paid users only */}
                        {user?.has_paid && (
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.15 }}
                            >
                                <CampaignProgressCard />
                            </motion.div>
                        )}

                        {/* Account Status */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            {user?.has_paid ? (
                                <AccountControls />
                            ) : (                                <Card className="border-border">
                                    <CardHeader>
                                        <CardTitle className="text-lg font-serif">Account Status</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Lock className="h-5 w-5" />
                                            <span className="font-medium">Free Preview Mode</span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            Pick a plan to unlock dispute letters, action plans, and live tracking.
                                        </p>
                                        <Button 
                                            className="w-full rounded-full btn-press"
                                            onClick={handleUpgrade}
                                            disabled={checkoutLoading}
                                            data-testid="sidebar-upgrade-btn"
                                        >
                                            <CreditCard className="h-4 w-4 mr-2" />
                                            See Plans · from {pricing.monthly_formatted || '…'}/mo
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}
                        </motion.div>

                        {/* Quick Tips */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <Card className="border-border">
                                <CardHeader>
                                    <CardTitle className="text-lg font-serif">Quick Tips</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="h-4 w-4 text-primary mt-0.5" />
                                        <p className="text-sm text-muted-foreground">
                                            Get your free credit reports from AnnualCreditReport.com
                                        </p>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="h-4 w-4 text-primary mt-0.5" />
                                        <p className="text-sm text-muted-foreground">
                                            Upload reports from all three bureaus for best results
                                        </p>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="h-4 w-4 text-primary mt-0.5" />
                                        <p className="text-sm text-muted-foreground">
                                            Bureaus must respond to disputes within 30 days
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </div>
                </div>
            </main>
            <PricingModal open={pricingOpen} onOpenChange={setPricingOpen} />
        </div>
    );
};

export default DashboardPage;
